/**
 * Editing in the grid — `KLQ-20260828-001`, modelled on ayon-frontend's
 * `CellEditingProvider.tsx`.
 *
 * **It is a machine, not a policy.** What it owns: which cell is open, the draft while it is
 * open, whether a large fan-out has been confirmed, and the undo stack. What it deliberately
 * does not own: what a column means, how a value is written, or which rows an edit reaches.
 * Those come from the table (which knows the columns and the selection) and from the surface
 * (which knows the endpoint). Ayon's split is the same one, and the reason it holds is that
 * its `ClipboardContext` calls `useCellEditing` rather than growing a second write path —
 * paste is a consumer of editing, and so is typing.
 *
 * **The batch is the unit of undo** (§F.2). One fan-out over twelve rows undoes as one act,
 * restoring each row's own previous value — which is why an entry carries a *map* of previous
 * values and not the single value that was written. Per-cell entries would mean twelve
 * `Cmd+Z` presses to undo one act, which is not what the person did.
 *
 * The stack is per surface and dies with the view. It is a convenience, not a document
 * history: the audit trail is the server's, and every write here lands in the task's own feed.
 */

import {
  createContext,
  useCallback,
  useContext,
  useMemo,
  useRef,
  useState,
} from "react";
import type { ReactNode } from "react";

import { cellKey, sameCell } from "./cell";
import type { CellKey, CellRef } from "./cell";

/** Rows that take the same value in one request. A fan-out is one group; an undo is several. */
export interface EditGroup {
  readonly rowIds: readonly string[];
  readonly value: unknown;
}

export interface EditBatch {
  readonly colKey: string;
  readonly groups: readonly EditGroup[];
}

export interface EditResult {
  readonly applied: readonly string[];
  /** Per row, in the server's words. One failing row never rolls back the others (§E.2). */
  readonly failed: readonly {
    readonly rowId: string;
    readonly reason: string;
  }[];
}

/** What the surface must provide: the one function that actually writes. */
export type EditWriter = (batch: EditBatch) => Promise<EditResult>;

/** Above this many rows, a fan-out asks once before it writes (§D.2). */
export const CONFIRM_ABOVE = 25;

export interface EditingApi {
  readonly editing: CellRef | null;
  readonly isEditing: (ref: CellRef) => boolean;
  readonly open: (ref: CellRef) => void;
  readonly cancel: () => void;
  /**
   * One act, however many columns and values it took.
   *
   * A typed edit is one batch of one group. A pasted block is one batch per column, each with
   * a group per distinct value — and it is deliberately **one** call, because what the person
   * did was paste once and `Cmd+Z` has to answer that rather than the shape of the request
   * that carried it. `previous` is keyed by cell, so the undo restores each cell's own value.
   */
  readonly apply: (edit: {
    readonly batches: readonly EditBatch[];
    readonly previous: ReadonlyMap<CellKey, unknown>;
  }) => Promise<void>;
  readonly undo: () => Promise<void>;
  readonly redo: () => Promise<void>;
  readonly canUndo: boolean;
  readonly canRedo: boolean;
  readonly pending: boolean;
  /** Rows the server refused, and why. Cleared when the row is edited again. */
  readonly failures: ReadonlyMap<string, string>;
  /**
   * Values written but not yet read back — `KLQ-20260828-001` §F.1.
   *
   * The table renders through these so a fan-out is visible at once instead of a round trip
   * later. It is **presentation state, not cache surgery**: `queries.ts` refuses to rewrite an
   * infinite query's pages for the board because that would also rewrite the loaded counts the
   * screen derives from them, and the same objection applies here. An overlay changes what one
   * cell looks like and nothing else — no count moves, no row changes page — and it evaporates
   * the moment the real rows arrive.
   */
  readonly optimistic: ReadonlyMap<CellKey, unknown>;
  /** The real rows have arrived; drop the overlay. Called by the table, not by a surface. */
  readonly settle: () => void;
}

interface HistoryEntry {
  readonly batches: readonly EditBatch[];
  readonly previous: ReadonlyMap<CellKey, unknown>;
}

/** Every row one batch touches, in the order the batch names them. */
function rowsOf(batch: EditBatch): string[] {
  return batch.groups.flatMap((group) => [...group.rowIds]);
}

const EditingContext = createContext<EditingApi | null>(null);

export function useOptionalEditing(): EditingApi | null {
  return useContext(EditingContext);
}

export function useEditing(): EditingApi {
  const api = useContext(EditingContext);
  if (api === null) {
    throw new Error("useEditing must be used within an <EditingProvider>");
  }
  return api;
}

/** Group rows by the value they are to be restored to — one request per distinct value. */
function groupByValue(
  colKey: string,
  rowIds: readonly string[],
  previous: ReadonlyMap<CellKey, unknown>,
): EditGroup[] {
  const buckets = new Map<string, { value: unknown; rowIds: string[] }>();
  for (const rowId of rowIds) {
    const value = previous.get(cellKey({ rowId, colKey })) ?? null;
    // The key only has to separate values that differ; JSON is enough for the shapes a cell
    // holds (a string, a date, an id, a list of ids) and it keeps this function total.
    const key = JSON.stringify(value ?? null);
    const bucket = buckets.get(key) ?? { value, rowIds: [] };
    bucket.rowIds.push(rowId);
    buckets.set(key, bucket);
  }
  return [...buckets.values()];
}

export function EditingProvider({
  write,
  children,
}: {
  readonly write: EditWriter;
  readonly children: ReactNode;
}) {
  const [editing, setEditing] = useState<CellRef | null>(null);
  const [pending, setPending] = useState(false);
  const [failures, setFailures] = useState<ReadonlyMap<string, string>>(
    new Map(),
  );
  const [optimistic, setOptimistic] = useState<ReadonlyMap<CellKey, unknown>>(
    new Map(),
  );
  const [undoStack, setUndoStack] = useState<readonly HistoryEntry[]>([]);
  const [redoStack, setRedoStack] = useState<readonly HistoryEntry[]>([]);

  // The write in flight. Two commits racing over the same column would interleave their
  // history entries, and the second undo would then restore the first edit's "previous".
  const inFlight = useRef(false);

  const run = useCallback(
    async (batches: readonly EditBatch[]): Promise<boolean> => {
      if (inFlight.current) return false;
      inFlight.current = true;
      setPending(true);
      // Show it immediately. Every cell the batches name takes its new value now; the ones
      // the server refuses are taken back below, individually, so a refusal on one row never
      // moves the eleven that succeeded.
      setOptimistic((current) => {
        const next = new Map(current);
        for (const batch of batches) {
          for (const group of batch.groups) {
            for (const rowId of group.rowIds) {
              next.set(cellKey({ rowId, colKey: batch.colKey }), group.value);
            }
          }
        }
        return next;
      });
      try {
        let wrote = false;
        for (const batch of batches) {
          const touched = rowsOf(batch);
          if (touched.length === 0) continue;
          const result = await write(batch);
          // Rolled back one row at a time, which is what §E.2 means by per-row failure: the
          // cell goes back to what it shows in the data, and the reason appears beside it.
          setOptimistic((current) => {
            if (result.failed.length === 0) return current;
            const next = new Map(current);
            for (const failure of result.failed) {
              next.delete(
                cellKey({ rowId: failure.rowId, colKey: batch.colKey }),
              );
            }
            return next;
          });
          wrote ||= result.applied.length > 0;
          setFailures((current) => {
            const next = new Map(current);
            // A row that has just been written successfully is no longer failing, whatever
            // it was told last time.
            for (const rowId of touched) next.delete(rowId);
            for (const failure of result.failed)
              next.set(failure.rowId, failure.reason);
            return next;
          });
        }
        return wrote;
      } finally {
        inFlight.current = false;
        setPending(false);
      }
    },
    [write],
  );

  const apply = useCallback<EditingApi["apply"]>(
    async ({ batches, previous }) => {
      if (batches.length === 0) return;
      const wrote = await run(batches);
      setEditing(null);
      if (!wrote) return;
      setUndoStack((stack) => [...stack, { batches, previous }]);
      // Any edit after an undo truncates the redo branch, as everywhere.
      setRedoStack([]);
    },
    [run],
  );

  const settle = useCallback(() => {
    setOptimistic((current) => (current.size === 0 ? current : new Map()));
  }, []);

  const undo = useCallback(async () => {
    const entry = undoStack[undoStack.length - 1];
    if (entry === undefined) return;
    const wrote = await run(
      entry.batches.map((batch) => ({
        colKey: batch.colKey,
        groups: groupByValue(batch.colKey, rowsOf(batch), entry.previous),
      })),
    );
    if (!wrote) return;
    setUndoStack((stack) => stack.slice(0, -1));
    setRedoStack((stack) => [...stack, entry]);
  }, [run, undoStack]);

  const redo = useCallback(async () => {
    const entry = redoStack[redoStack.length - 1];
    if (entry === undefined) return;
    const wrote = await run(entry.batches);
    if (!wrote) return;
    setRedoStack((stack) => stack.slice(0, -1));
    setUndoStack((stack) => [...stack, entry]);
  }, [redoStack, run]);

  const api = useMemo<EditingApi>(
    () => ({
      editing,
      isEditing: (ref) => sameCell(editing, ref),
      open: setEditing,
      cancel: () => {
        setEditing(null);
      },
      apply,
      undo,
      redo,
      canUndo: undoStack.length > 0,
      canRedo: redoStack.length > 0,
      pending,
      failures,
      optimistic,
      settle,
    }),
    [
      apply,
      editing,
      failures,
      optimistic,
      pending,
      redo,
      redoStack.length,
      settle,
      undo,
      undoStack.length,
    ],
  );

  return (
    <EditingContext.Provider value={api}>{children}</EditingContext.Provider>
  );
}
